import { useState, useEffect, useCallback } from 'react';
import { FaBell } from 'react-icons/fa';
import Card from '../../../components/Card/Card';
import { useParams, useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import NotificationForm from './NotificationForm';
import { fetchPrograms } from '../../../redux/program/programActions';
import { useDispatch, useSelector } from 'react-redux';
import { getNotificationById, createNotification, updateNotification, bulkUploadNotification } from '../../../services/AppManagement/NotificationService';
import { getClassWiseStudentList, getStudentsByTransportAndActivation } from '../../../services/StudentService';
import { getStudentsByAttendanceStatus, getAttendanceLabelList } from '../../../services/AppManagement/AttendanceService';
import ExcelBulkUpload from '../../ExcelBulkUpload/ExcelBulkUpload';
import * as XLSX from 'xlsx';
import { getCircularAvailableplaceholders } from '../../../services/AppManagement/CircularService';

const NotificationManage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const state = useSelector((state) => state.program);
  const programData = state.programs.data;
  const params = useParams();
  const [student_ids, setStudentIds] = useState([]);
  const [notice, setNotice] = useState('');
  const [studentList, setStudentList] = useState([]);
  const [student_loader, setStudentLoader] = useState(false);
  const [program_id, setProgramId] = useState('');
  const [mode_of_transport, setModeOfTransport] = useState('');
  const [is_activated, setIsActivated] = useState('');
  const [vehicle_number, setVehicleNumber] = useState('');
  const [attendance_date, setAttendanceDate] = useState('');
  const [attendance_status, setAttendanceStatus] = useState('');
  const [attendanceStatusOptions, setAttendanceStatusOptions] = useState([]);
  const [isBulk, setIsBulk] = useState(false);
  const [data, setData] = useState([]);
  const [availableplaceholders, setAvailableplaceholders] = useState([]);
  const id = params.id;

  const getUniqueNotification = useCallback(async () => {
    try {
      const response = await getNotificationById(id);
      if (response.status === 200) {
        const data = response.data.data;
        setProgramId(data.program_id);
        setNotice(data.message);
        setStudentIds([data.student_id.toString()]);
        let student_list = [data.student];
        setStudentList(student_list);
      }
    } catch (error) {
      console.error(error);
    }
  }, [id]);

  const fetchStudentList = async (program_id) => {
    try {
      setStudentLoader(true);
      const response = await getClassWiseStudentList(program_id);
      if (response.status === 200) {
        setStudentList(response.data.data || []);
      } else {
        setStudentList([]);
      }
    } catch (error) {
      console.error(error);
      setStudentList([]);
    } finally {
      setStudentIds([]);
      setStudentLoader(false);
    }
  };

  const fetchStudentListByTransportStatus = async (transport, activated, vehicleNumber) => {
    try {
      setStudentLoader(true);
      const response = await getStudentsByTransportAndActivation(transport, activated, vehicleNumber);
      if (response.status === 200) {
        setStudentList(response.data.data || []);
      } else {
        setStudentList([]);
      }
    } catch (error) {
      console.error(error);
      setStudentList([]);
    } finally {
      setStudentIds([]);
      setStudentLoader(false);
    }
  };

  const fetchStudentListByAttendanceStatus = async (date, status) => {
    try {
      setStudentLoader(true);
      const response = await getStudentsByAttendanceStatus(date, status);
      if (response.status === 200) {
        // Confirmed response shape (2026-10-30): { data: { students: [{ student_id,
        // id_number, student_name, program_id, class_name, roll_no }] } } — mapped
        // to the field names the shared student table expects (id/first_name/
        // last_name) instead of adding a second rendering path for this list.
        const students = response.data.data?.students || [];
        const mapped = students.map((student) => ({
          id: student.student_id,
          id_number: student.id_number,
          first_name: student.student_name,
          last_name: '',
          roll_no: student.roll_no,
          program_id: student.program_id,
          class_name: student.class_name,
        }));
        setStudentList(mapped);
      } else {
        setStudentList([]);
      }
    } catch (error) {
      console.error(error);
      setStudentList([]);
    } finally {
      setStudentIds([]);
      setStudentLoader(false);
    }
  };

  // Same label source as the attendance-marking screen (AttendanceManage.js's
  // fetchAttendanceLabelList), so this filter offers the exact statuses a
  // student could have been marked with, not a hardcoded Present/Absent pair.
  const fetchAttendanceStatusOptions = useCallback(async () => {
    try {
      const response = await getAttendanceLabelList();
      if (response.status === 200) {
        const labels = (response.data.data || [])
          .filter((item) => item.label_name && item.label_name !== 'Not Marked')
          .map((item) => ({ id: item.label_name.toLowerCase(), name: item.label_name }));
        setAttendanceStatusOptions(labels);
      }
    } catch (error) {
      console.error('Error fetching attendance label list:', error);
    }
  }, []);

  const fetchAvailableplaceholders = useCallback(async () => {
    try {
      const response = await getCircularAvailableplaceholders();
      if (response.status === 200) {
        const placeholders = response.data.data;
        setAvailableplaceholders(placeholders);
      }
    } catch (error) {
      console.error("Error fetching available placeholders:", error);
    }
  }, []);

  const changeHandler = (event) => {
    const { name, value } = event.target;
    if (name === 'program_id') {
      setProgramId(value);
      setModeOfTransport('');
      setIsActivated('');
      setVehicleNumber('');
      setAttendanceDate('');
      setAttendanceStatus('');
      fetchStudentList(value);
    } else if (name === 'mode_of_transport') {
      setModeOfTransport(value);
      setProgramId('');
      setAttendanceDate('');
      setAttendanceStatus('');
      const nextVehicleNumber = value === 'bus' ? vehicle_number : '';
      setVehicleNumber(nextVehicleNumber);
      if (value || is_activated || nextVehicleNumber) {
        fetchStudentListByTransportStatus(value, is_activated, nextVehicleNumber);
      } else {
        setStudentList([]);
      }
    } else if (name === 'is_activated') {
      setIsActivated(value);
      setProgramId('');
      setAttendanceDate('');
      setAttendanceStatus('');
      if (mode_of_transport || value || vehicle_number) {
        fetchStudentListByTransportStatus(mode_of_transport, value, vehicle_number);
      } else {
        setStudentList([]);
      }
    } else if (name === 'vehicle_number') {
      setVehicleNumber(value);
      setProgramId('');
      setAttendanceDate('');
      setAttendanceStatus('');
    } else if (name === 'attendance_date') {
      setAttendanceDate(value);
      setProgramId('');
      setModeOfTransport('');
      setIsActivated('');
      setVehicleNumber('');
      if (value && attendance_status) {
        fetchStudentListByAttendanceStatus(value, attendance_status);
      } else {
        setStudentList([]);
      }
    } else if (name === 'attendance_status') {
      setAttendanceStatus(value);
      setProgramId('');
      setModeOfTransport('');
      setIsActivated('');
      setVehicleNumber('');
      if (attendance_date && value) {
        fetchStudentListByAttendanceStatus(attendance_date, value);
      } else {
        setStudentList([]);
      }
    } else {
      setNotice(value);
    }
  };

  const resetFilters = () => {
    setProgramId('');
    setModeOfTransport('');
    setIsActivated('');
    setVehicleNumber('');
    setAttendanceDate('');
    setAttendanceStatus('');
    setStudentList([]);
    setStudentIds([]);
  };

  const vehicleNumberSearchHandler = (event) => {
    if (event.key && event.key !== 'Enter') return;
    setProgramId('');
    if (mode_of_transport || is_activated || vehicle_number) {
      fetchStudentListByTransportStatus(mode_of_transport, is_activated, vehicle_number);
    } else {
      setStudentList([]);
    }
  };

  const handleInputChange = (event) => {
    const { value, checked } = event.target;
    const student_id = value;
    if (checked) {
      setStudentIds([...student_ids, student_id]);
    } else {
      const updatedStudentIds = student_ids.filter((id) => id !== student_id);
      setStudentIds(updatedStudentIds);
    }
  };

  const selectAllStudent = (event) => {
    const student_ids = [];
    if (event.target.checked) {
      studentList.forEach((student) => {
        const id = student.id.toString();
        student_ids.push(id);
      });
    }
    setStudentIds(student_ids);
  };

  useEffect(() => {
    fetchAvailableplaceholders();
    fetchAttendanceStatusOptions();
    dispatch(fetchPrograms());
    if (id) {
      getUniqueNotification();
    }
  }, [dispatch, fetchAvailableplaceholders, fetchAttendanceStatusOptions, getUniqueNotification, id]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    let data = {
      notice: notice,
      session_id: localStorage.getItem('session_id'),
    };
    if (id) {
      data.student_id = student_ids[0];
    }
    else {
      data.student_ids = student_ids;
    }

    try {
      const response = id
        ? await updateNotification(id, { data })
        : await createNotification({ data });
      if (response.status === 200) {
        let msg = response.data.message.success;
        enqueueSnackbar(msg, { variant: 'success' });
        navigate('/notification/list');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const back = () => {
    navigate('/notification/list');
  };

  const downloadExcel = (e) => {
    e.preventDefault();
    const data = studentList.map((student) => ({
      'id_number': student.id_number,
      'first_name': student.first_name,
      'last_name': student.last_name,
      'roll_no': student.roll_no,
      'notice_type': 'Notification',
      'notice': ""
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `student-bulk-upload-notification.xlsx`);
  }

  const excelFileReader = (event) => {
    const file = event.target.files[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e) => {
      const binaryStr = e.target.result;
      const workbook = XLSX.read(binaryStr, { type: 'binary' });

      const sheetName = workbook.SheetNames[0]; // Read the first sheet
      const sheet = workbook.Sheets[sheetName];

      const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' }); // defval ensures empty cells are included
      setData(jsonData);
    };

    reader.readAsBinaryString(file);
  };

  const bulkUploadFile = async () => {
    try {
      const response = await bulkUploadNotification(data);
      if (response.status === 200) {
        const data = await response.json();
        enqueueSnackbar(data.message.success, { variant: 'success' });
      }
    } catch (error) {
      console.error(error);
    }
  }
  const bulkChangeHandler = () => {
    studentList.length > 0 ? setIsBulk(!isBulk) : enqueueSnackbar('Please select class to upload bulk notification', { variant: 'error' });
  };

  return (
    <Card componentName={id ? 'Update Notifiaction' : 'Add Notifiaction'} icon={<FaBell />} back={back}>
      {isBulk ?
        <ExcelBulkUpload
          downloadExcel={downloadExcel}
          excelFileReader={excelFileReader}
          bulkUploadFile={bulkUploadFile}
          loader={student_loader}
        /> :
        <NotificationForm
          bulkChangeHandler={bulkChangeHandler}
          isBulk={isBulk}
          submitHandler={handleSubmit}
          changeHandler={changeHandler}
          handleInputChange={handleInputChange}
          selectAllStudent={selectAllStudent}
          id={id}
          programData={programData}
          program_id={program_id}
          mode_of_transport={mode_of_transport}
          is_activated={is_activated}
          vehicle_number={vehicle_number}
          vehicleNumberSearchHandler={vehicleNumberSearchHandler}
          resetFilters={resetFilters}
          isAnyFilterActive={Boolean(program_id || mode_of_transport || is_activated || vehicle_number || attendance_date || attendance_status)}
          attendance_date={attendance_date}
          attendance_status={attendance_status}
          attendanceStatusOptions={attendanceStatusOptions}
          isAttendanceFilter={Boolean(attendance_date && attendance_status)}
          isClassDisabled={Boolean(mode_of_transport || is_activated || vehicle_number || attendance_date || attendance_status)}
          isTransportFilterDisabled={Boolean(program_id || attendance_date || attendance_status)}
          isAttendanceFilterDisabled={Boolean(program_id || mode_of_transport || is_activated || vehicle_number)}
          notice={notice}
          student_ids={student_ids}
          studentList={studentList}
          student_loader={student_loader}
          availableplaceholders={availableplaceholders}
        />}
    </Card>
  );
};
export default NotificationManage;
